import React, { useMemo, useState } from 'react';
import { FaCheck, FaLink, FaXmark } from 'react-icons/fa6';
import { useModal } from '@features/Modal/lib/ModalProvider';
import { ModalProps, useCloseClassName } from '@features/Modal';
import { getApiError, ISettingsAccountsDto, IServiceKycResponse } from '@shared/api';
import { Button, Checkbox, Input } from '@shared/UI';
import { useNotify } from '@features/Notify';
import { KYCResponse } from './components';
import { accountVerify, accountReject, accountApprove } from '../../api';
import { AccountAction } from '../../lib';
import './AccountsActionModal.scss';

interface AccountsActionModalProps extends ModalProps {
    account: ISettingsAccountsDto;
    action: AccountAction;
    refresh: () => void;
    AdditionalComponent?: React.FC | null;
}

export const AccountsActionModal: React.FC<AccountsActionModalProps> = (props) => {
    const { account, action, refresh, AdditionalComponent } = props;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [checked, setChecked] = useState<boolean>(false);
    const [comment, setComment] = useState<string>('');
    const [response, setResponse] = useState<null | IServiceKycResponse>(null);
    const notify = useNotify();
    const { removeModal } = useModal();

    const actions = useMemo(
        () => ({
            approve: {
                title: 'Approval',
                description: 'Enter information pertinent to the approval of the new account.',
                icon: <FaCheck color='#23B383' />,
                buttonTitle: 'Approve',
                action: accountApprove,
                withResponse: false,
                checkboxRequired: true,
                checkboxTitle: 'CAIS report for this account has been submitted',
            },
            reject: {
                title: 'Rejection',
                description:
                    'You are about to reject the creating a new account. Please select the reason of the decision.',
                icon: <FaXmark color='#EF4444' />,
                buttonTitle: 'Reject',
                action: accountReject,
                withResponse: false,
                checkboxRequired: false,
                checkboxTitle: null,
            },
            kyc: {
                title: 'Send to KYC',
                description: null,
                icon: <FaLink color='#1293DE' />,
                buttonTitle: 'Send',
                action: accountVerify,
                withResponse: true,
                checkboxRequired: false,
                checkboxTitle: null,
            },
        }),
        [],
    );

    useCloseClassName({
        id: account.id.toString(),
        className: 'widget-accounts-action-modal__content__buttons-container__cancel',
    });

    const onSubmit = () => {
        setIsLoading(true);
        if (actions[action].withResponse) {
            actions[action]
                .action({ id: account.id, comment })
                .catch((e) => notify.error(getApiError(e)))
                .then((data) => {
                    if (data && 'message' in data && 'result' in data) {
                        setResponse(data);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                    refresh();
                });
            return;
        }
        actions[action]
            .action({ id: account.id, comment })
            .catch((e) => notify.error(getApiError(e)))
            .then()
            .finally(() => {
                setIsLoading(false);
                refresh();
                removeModal();
            });
    };

    if (action === 'kyc' && response) {
        return (
            <div className='widget-accounts-action-modal'>
                <KYCResponse response={response} />
            </div>
        );
    }

    return (
        <div className='widget-accounts-action-modal'>
            <div className='widget-accounts-action-modal__header'>
                <h3 className='widget-accounts-action-modal__title'>
                    {actions[action].icon} {actions[action].title}
                </h3>
                <h5 className='widget-accounts-action-modal__sub-title'>
                    <span>Account ID: </span>
                    {account.clearingAccount}
                </h5>
            </div>
            <div className='widget-accounts-action-modal__content'>
                {response ? (
                    <div className='widget-accounts-action-modal__content__comment-container'>
                        <p>
                            {response?.result}: {response?.message}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className='widget-accounts-action-modal__content__comment-container'>
                            <p>{actions[action].description}</p>
                            {!!AdditionalComponent && <AdditionalComponent />}
                            <Input
                                placeholder={`${actions[action].title} Reason`}
                                textarea
                                value={comment}
                                wrapperClassName='widget-accounts-action-modal__content__comment-container__textarea-wrap'
                                onChange={(val) => setComment(val.target.value)}
                            />
                        </div>
                        {actions[action].checkboxRequired && (
                            <Checkbox
                                label={actions[action].checkboxTitle}
                                value={checked}
                                onChange={() => setChecked((prev) => !prev)}
                            />
                        )}
                        <div className='widget-accounts-action-modal__content__buttons-container'>
                            <Button
                                className='widget-accounts-action-modal__content__buttons-container__cancel'
                                onClick={removeModal}
                            >
                                Cancel
                            </Button>
                            <Button
                                loading={isLoading}
                                disabled={actions[action].checkboxRequired && !checked}
                                onClick={onSubmit}
                            >
                                {actions[action].buttonTitle}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
